import React, { useEffect, useState, useRef } from 'react';
import { Box, CircularProgress, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Button, TextField, MenuItem, Tabs, Tab, Divider } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import api from '../../services/api';
import { exportCsv } from '../../utils/exportCsv';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const REPORT_TYPES = [
  { key: 'risks', label: 'Risk Register' },
  { key: 'she', label: 'SHE Register' },
  { key: 'accidents', label: 'Accident Register' },
  { key: 'losses', label: 'Loss Events' },
  { key: 'bcm', label: 'BCM Plans' },
  { key: 'audit', label: 'Audit Trail' },
];

const REPORT_COLUMNS = {
  risks: [
    { key: 'sn', label: 'Risk ID' },
    { key: 'risk_description', label: 'Description' },
    { key: 'category', label: 'Category' },
    { key: 'department_id', label: 'Department' },
    { key: 'owner', label: 'Owner' },
    { key: 'inherent_risk_score', label: 'Inherent Score' },
    { key: 'residual_risk_score', label: 'Residual Score' },
    { key: 'status', label: 'Status' },
    { key: 'created_at', label: 'Date Created' },
  ],
  she: [
    { key: 'action_id', label: 'Action ID' },
    { key: 'date', label: 'Date' },
    { key: 'activity_category', label: 'Category' },
    { key: 'location', label: 'Location' },
    { key: 'department', label: 'Department' },
    { key: 'description', label: 'Description' },
    { key: 'owner', label: 'Owner' },
    { key: 'priority', label: 'Priority' },
    { key: 'status', label: 'Status' },
  ],
  losses: [
    { key: 'case_number', label: 'Case Number' },
    { key: 'loss_reference', label: 'Reference' },
    { key: 'event_title', label: 'Event Title' },
    { key: 'department_id', label: 'Department' },
    { key: 'loss_date', label: 'Loss Date' },
    { key: 'case_category', label: 'Case Category' },
    { key: 'misconduct_type', label: 'Misconduct Type' },
    { key: 'police_ref', label: 'Police Reference' },
    { key: 'complainant', label: 'Complainant' },
    { key: 'accused_person', label: 'Accused Person' },
    { key: 'estimated_loss_value', label: 'Est. Loss Value' },
    { key: 'estimated_recovery_value', label: 'Est. Recovery Value' },
    { key: 'financial_impact', label: 'Financial Impact' },
    { key: 'non_financial_impact', label: 'Non-Financial Impact' },
    { key: 'corrective_action_recommendation', label: 'Action Recommendation' },
    { key: 'corrective_action_taken', label: 'Action Taken' },
    { key: 'root_cause', label: 'Root Cause' },
    { key: 'status', label: 'Status' },
  ],
  bcm: [
    { key: 'plan_reference', label: 'Reference' },
    { key: 'plan_name', label: 'Plan Name' },
    { key: 'department_id', label: 'Department' },
    { key: 'critical_process', label: 'Critical Process' },
    { key: 'rto_hours', label: 'RTO (hrs)' },
    { key: 'rpo_hours', label: 'RPO (hrs)' },
    { key: 'readiness_score', label: 'Readiness' },
    { key: 'plan_status', label: 'Status' },
  ],
  accidents: [
    { key: 'iod_number', label: 'IOD Number' },
    { key: 'name_of_injured', label: 'Name of Injured' },
    { key: 'date_of_injury', label: 'Date of Injury' },
    { key: 'time_of_injury', label: 'Time' },
    { key: 'department', label: 'Department' },
    { key: 'designation', label: 'Designation' },
    { key: 'source_of_injury', label: 'Source of Injury' },
    { key: 'nature_of_injury', label: 'Nature of Injury' },
    { key: 'part_of_body_injured', label: 'Body Part Injured' },
    { key: 'days_lost', label: 'Days Lost' },
    { key: 'medical_treatment', label: 'Medical Treatment' },
    { key: 'manager_supervisor', label: 'Manager/Supervisor' },
  ],
  audit: [
    { key: 'timestamp', label: 'Timestamp' },
    { key: 'initiator', label: 'User' },
    { key: 'action', label: 'Action' },
    { key: 'details', label: 'Details' },
    { key: 'ip', label: 'IP Address' },
    { key: 'status', label: 'Status' },
  ],
};

const REPORT_ENDPOINTS = {
  risks: '/reports/risks',
  she: '/reports/she-events',
  accidents: '/reports/accidents',
  losses: '/reports/loss-events',
  bcm: '/reports/bcm-plans',
  audit: '/reports/audit-trail',
};

const ReportsAnalytics = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Determine user role
  const userRoles = (user?.roles || []).map(r => r.toLowerCase());
  const isSHE = userRoles.some(r => r.includes('she'));
  const isRisk = userRoles.some(r => r.includes('risk'));
  const isBCM = userRoles.some(r => r.includes('bcm'));
  const isAdmin = userRoles.some(r => ['system admin', 'admin', 'administrator'].includes(r));

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const response = await api.get('/reports/summary');
        setData(response.data);
      } catch (error) {
        console.error('Failed to load reports:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, []);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress sx={{ color: '#10b981' }} /></Box>;
  }

  const money = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value || 0));
  const palette = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#64748b'];

  const series = (items, label) => ({
    labels: (items || []).map(item => item.label || 'Unassigned'),
    datasets: [{
      label,
      data: (items || []).map(item => Number(item.value || 0)),
      backgroundColor: palette,
      borderWidth: 0,
      borderRadius: 6
    }]
  });

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { color: '#94a3b8', usePointStyle: true } },
      tooltip: { backgroundColor: '#0f172a', titleColor: 'white', bodyColor: 'white' }
    },
    scales: {
      x: { ticks: { color: '#94a3b8' }, grid: { display: false } },
      y: { ticks: { color: '#94a3b8' }, grid: { color: 'rgba(255,255,255,0.06)' } }
    }
  };

  const doughnutOptions = {
    ...chartOptions,
    scales: undefined
  };

  const ChartCard = ({ title, children }) => (
    <Paper className="glass" sx={{ p: 3, borderRadius: '18px', background: 'rgba(30,41,59,0.45)', height: 360 }}>
      <Typography sx={{ color: 'white', fontWeight: 'bold', mb: 2 }}>{title}</Typography>
      <Box sx={{ height: 280 }}>{children}</Box>
    </Paper>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" className="gradient-text" sx={{ fontWeight: 'bold', mb: 3 }}>Analysis & Reports</Typography>

      {(() => {
        const visibleCards = (data?.cards || []).filter(card => {
          const val = Number(card.value || 0);
          if (val <= 0) return false;
          const label = card.label.toLowerCase();
          if (isAdmin) return true;
          if (isSHE && label.includes('she')) return true;
          if (isRisk && (label.includes('risk') || label.includes('loss') || label.includes('treatment'))) return true;
          if (isBCM && label.includes('bcm')) return true;
          return false;
        });
        const cardCount = visibleCards.length;
        return (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {visibleCards.map(card => (
              <Grid item xs={12} sm={6} md={cardCount <= 4 ? 6 : 4} lg={cardCount <= 4 ? 3 : 2.4} key={card.label}>
                <Paper className="glass" sx={{ p: 2.5, borderRadius: '16px', background: 'rgba(30,41,59,0.45)', minHeight: 112 }}>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem' }}>{card.label}</Typography>
                  <Typography sx={{ color: card.label.includes('Loss Value') ? '#ef4444' : '#f8fafc', fontSize: '1.35rem', fontWeight: 'bold', mt: 1 }}>
                    {card.label.includes('Loss Value') ? money(card.value) : card.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        );
      })()}

      {(() => {
        // Count visible charts for dynamic sizing
        const visibleCharts = [
          (isAdmin || isRisk) && (data?.lossTrend || []).length > 0,
          (isAdmin || isRisk) && (data?.lossByDepartment || []).length > 0,
          (isAdmin || isRisk) && (data?.treatmentPerformance || []).length > 0,
          (isAdmin || isBCM) && (data?.bcmStatus || []).length > 0,
          (isAdmin || isSHE) && (data?.sheCompliance || []).length > 0
        ].filter(Boolean);
        const chartCount = visibleCharts.length;
        return (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {(isAdmin || isRisk) && (data?.lossTrend || []).length > 0 && (
              <Grid item xs={12} md={6}>
                <ChartCard title="Loss Trend">
                  <Bar data={series(data.lossTrend, 'Financial Loss')} options={chartOptions} />
                </ChartCard>
              </Grid>
            )}
            {(isAdmin || isRisk) && (data?.lossByDepartment || []).length > 0 && (
              <Grid item xs={12} md={6}>
                <ChartCard title="Loss by Department">
                  <Bar data={series(data.lossByDepartment, 'Loss Value')} options={chartOptions} />
                </ChartCard>
              </Grid>
            )}
            {(isAdmin || isRisk) && (data?.treatmentPerformance || []).length > 0 && (
              <Grid item xs={12} md={6}>
                <ChartCard title="Treatment Performance">
                  <Doughnut data={series(data.treatmentPerformance, 'Controls')} options={doughnutOptions} />
                </ChartCard>
              </Grid>
            )}
            {(isAdmin || isBCM) && (data?.bcmStatus || []).length > 0 && (
              <Grid item xs={12} md={6}>
                <ChartCard title="BCM Status">
                  <Doughnut data={series(data.bcmStatus, 'Plans')} options={doughnutOptions} />
                </ChartCard>
              </Grid>
            )}
            {(isAdmin || isSHE) && (data?.sheCompliance || []).length > 0 && (
              <Grid item xs={12} md={6}>
                <ChartCard title="SHE Compliance Status">
                  <Doughnut data={series(data.sheCompliance, 'SHE Records')} options={doughnutOptions} />
                </ChartCard>
              </Grid>
            )}
          </Grid>
        );
      })()}

      {/* ── Report Center ── */}
      <Divider sx={{ my: 5, borderColor: 'rgba(255,255,255,0.08)' }} />
      <ReportCenter user={user} />
    </Box>
  );
};

/* ─── Report Center sub-component ─── */
const ReportCenter = ({ user }) => {
  const isAdmin = (user?.roles || []).some(r =>
    ['system admin', 'admin', 'administrator'].includes(r.toLowerCase())
  );

  const availableTypes = REPORT_TYPES.filter(t => t.key !== 'audit' || isAdmin);

  const [activeReport, setActiveReport] = useState(availableTypes[0]?.key || 'risks');
  const [reportData, setReportData] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [departments, setDepartments] = useState([]);
  
  // Dynamic filter options from database
  const [filterOptions, setFilterOptions] = useState({
    category: [],
    status: [],
    inherent_likelihood: [],
    inherent_consequence: [],
    inherent_risk_scores: [],
    activity_category: [],
    priority: [],
    source_of_injury: [],
    nature_of_injury: [],
    case_category: [],
    misconduct_type: [],
    priority_level: [],
    plan_status: [],
  });
  
  // Additional filters for specific reports
  // SHE Register filters
  const [sheCategoryFilter, setSheCategoryFilter] = useState('');
  const [shePriorityFilter, setShePriorityFilter] = useState('');
  
  // Accident Register filters
  const [accidentSourceFilter, setAccidentSourceFilter] = useState('');
  const [accidentNatureFilter, setAccidentNatureFilter] = useState('');
  
  // Risk Register filters
  const [riskCategoryFilter, setRiskCategoryFilter] = useState('');
  const [riskLikelihoodFilter, setRiskLikelihoodFilter] = useState('');
  const [riskConsequenceFilter, setRiskConsequenceFilter] = useState('');
  const [riskScoreMin, setRiskScoreMin] = useState('');
  const [riskScoreMax, setRiskScoreMax] = useState('');
  
  const printRef = useRef();

  // Fetch departments on mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const resp = await api.get('/departments');
        setDepartments(resp.data?.data || resp.data || []);
      } catch (err) {
        console.error('Failed to load departments:', err);
      }
    };
    fetchDepartments();
  }, []);
  
  // Fetch filter options when report type changes
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const resp = await api.get('/reports/filter-options', { params: { report: activeReport } });
        setFilterOptions(prev => ({ ...prev, ...resp.data }));
      } catch (err) {
        console.error('Failed to load filter options:', err);
      }
    };
    fetchFilterOptions();
  }, [activeReport]);

  const fetchReport = async (type, from, to, dept, status) => {
    setReportLoading(true);
    setReportError('');
    setReportData([]);
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (dept) params.department_id = dept;
      if (status) params.status = status;
      
      // SHE specific filters
      if (type === 'she') {
        if (sheCategoryFilter) params.activity_category = sheCategoryFilter;
        if (shePriorityFilter) params.priority = shePriorityFilter;
      }
      
      // Accident specific filters
      if (type === 'accidents') {
        if (accidentSourceFilter) params.source_of_injury = accidentSourceFilter;
        if (accidentNatureFilter) params.nature_of_injury = accidentNatureFilter;
      }
      
      // Risk specific filters
      if (type === 'risks') {
        if (riskCategoryFilter) params.category = riskCategoryFilter;
        if (riskLikelihoodFilter) params.inherent_likelihood = riskLikelihoodFilter;
        if (riskConsequenceFilter) params.inherent_consequence = riskConsequenceFilter;
        if (riskScoreMin) params.risk_score_min = riskScoreMin;
        if (riskScoreMax) params.risk_score_max = riskScoreMax;
      }
      
      const resp = await api.get(REPORT_ENDPOINTS[type], { params });
      setReportData(resp.data || []);
    } catch (err) {
      if (err.response?.status === 403) {
        setReportError(err.response.data?.error || 'Access denied.');
      } else {
        setReportError('Failed to load report data.');
      }
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(activeReport, fromDate, toDate, departmentFilter, statusFilter);
  // eslint-disable-next-line
  }, [activeReport]);

  const handleGenerate = () => fetchReport(activeReport, fromDate, toDate, departmentFilter, statusFilter);

  const columns = REPORT_COLUMNS[activeReport] || [];
  const reportLabel = availableTypes.find(t => t.key === activeReport)?.label || activeReport;

  const handleCsvExport = () => {
    const filename = `${reportLabel.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
    exportCsv(reportData, filename, columns);
  };

  const getRiskColor = (score) => {
    const s = typeof score === 'string' ? score.toLowerCase() : '';
    if (s === 'extreme' || s === 'critical') return { bg: '#c0392b', text: 'white' };
    if (s === 'high') return { bg: '#e67e22', text: 'white' };
    if (s === 'tolerable' || s === 'medium') return { bg: '#f1c40f', text: '#1a1a1a' };
    if (s === 'low') return { bg: '#27ae60', text: 'white' };
    const n = Number(score);
    if (!isNaN(n)) {
      if (n >= 15) return { bg: '#c0392b', text: 'white' };
      if (n >= 9) return { bg: '#e67e22', text: 'white' };
      if (n >= 4) return { bg: '#f1c40f', text: '#1a1a1a' };
      return { bg: '#27ae60', text: 'white' };
    }
    return { bg: '#64748b', text: 'white' };
  };

  const riskBadge = (val) => {
    const c = getRiskColor(val);
    return `<span style="background:${c.bg};color:${c.text};padding:3px 10px;border-radius:4px;font-weight:bold;font-size:11px;display:inline-block">${val ?? '—'}</span>`;
  };

  const statusBadge = (val) => {
    const s = (val || '').toLowerCase();
    const bg = s === 'open' ? '#e67e22' : s === 'closed' ? '#27ae60' : s === 'improving' ? '#3b82f6' : '#64748b';
    return `<span style="background:${bg};color:white;padding:3px 10px;border-radius:4px;font-weight:bold;font-size:11px;display:inline-block">${val ?? '—'}</span>`;
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const now = new Date();
    const monthYear = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const generated = now.toLocaleString();

    // Build active filters list
    const activeFilters = [];
    if (fromDate) activeFilters.push(`From: ${fromDate}`);
    if (toDate) activeFilters.push(`To: ${toDate}`);
    if (departmentFilter) {
      const dept = departments.find(d => (d.department_id || d.id) == departmentFilter);
      activeFilters.push(`Department: ${dept?.department_name || dept?.name || departmentFilter}`);
    }
    if (statusFilter) activeFilters.push(`Status: ${statusFilter}`);
    if (activeReport === 'she') {
      if (sheCategoryFilter) activeFilters.push(`Category: ${sheCategoryFilter}`);
      if (shePriorityFilter) activeFilters.push(`Priority: ${shePriorityFilter}`);
    }
    if (activeReport === 'accidents') {
      if (accidentSourceFilter) activeFilters.push(`Source: ${accidentSourceFilter}`);
      if (accidentNatureFilter) activeFilters.push(`Nature: ${accidentNatureFilter}`);
    }
    if (activeReport === 'risks') {
      if (riskCategoryFilter) activeFilters.push(`Category: ${riskCategoryFilter}`);
      if (riskLikelihoodFilter) activeFilters.push(`Likelihood: ${riskLikelihoodFilter}`);
      if (riskConsequenceFilter) activeFilters.push(`Consequence: ${riskConsequenceFilter}`);
      if (riskScoreMin || riskScoreMax) activeFilters.push(`Score: ${riskScoreMin || '1'} – ${riskScoreMax || '25'}`);
    }
    const filterLine = activeFilters.length ? activeFilters.join('  |  ') : 'All Records';

    // ── Section builders per report type ──
    const buildRiskSections = () => {
      const total = reportData.length;
      const high = reportData.filter(r => ['extreme','high'].includes((r.inherent_risk_score||'').toLowerCase())).length;
      const tolerable = reportData.filter(r => (r.inherent_risk_score||'').toLowerCase() === 'tolerable').length;
      const low = reportData.filter(r => (r.inherent_risk_score||'').toLowerCase() === 'low').length;
      const open = reportData.filter(r => (r.status||'').toLowerCase() === 'open').length;

      const execSummary = `
        <div class="page-break section">
          <div class="section-header dark">EXECUTIVE RISK SUMMARY <span style="float:right;font-size:13px;font-weight:400">${monthYear}</span></div>
          <div style="display:flex;gap:16px;margin:20px 0">
            <div class="stat-card" style="background:#1e3a5f"><div class="stat-num">${total}</div><div class="stat-label">TOTAL RISKS</div><div class="stat-sub">All Departments</div></div>
            <div class="stat-card" style="background:#e67e22"><div class="stat-num">${high}</div><div class="stat-label">HIGH</div><div class="stat-sub">Priority Action</div></div>
            <div class="stat-card" style="background:#f1c40f;color:#1a1a1a"><div class="stat-num" style="color:#1a1a1a">${tolerable}</div><div class="stat-label" style="color:#1a1a1a">TOLERABLE</div><div class="stat-sub" style="color:#555">Monitor</div></div>
            <div class="stat-card" style="background:#27ae60"><div class="stat-num">${low}</div><div class="stat-label">LOW</div><div class="stat-sub">Monitor</div></div>
            <div class="stat-card" style="background:#1e3a5f"><div class="stat-num" style="color:#f1c40f">${open}</div><div class="stat-label" style="color:#f1c40f">OPEN</div><div class="stat-sub">Requires Monitoring</div></div>
          </div>
          <table>
            <thead><tr class="dark-header"><th>RISK LEVEL</th><th>INHERENT</th><th>RESIDUAL</th><th>OPEN</th><th>IMPROVING</th><th>CLOSED</th></tr></thead>
            <tbody>
              ${['Extreme','High','Tolerable','Low'].map(level => {
                const lvlData = reportData.filter(r => (r.inherent_risk_score||'').toLowerCase() === level.toLowerCase());
                const resLvl = reportData.filter(r => (r.residual_risk_score||'').toLowerCase() === level.toLowerCase());
                const c = getRiskColor(level);
                return `<tr>
                  <td><span style="background:${c.bg};color:${c.text};padding:4px 14px;border-radius:5px;font-weight:bold;font-size:12px">${level.toUpperCase()}</span></td>
                  <td style="text-align:center;font-weight:bold">${lvlData.length}</td>
                  <td style="text-align:center;font-weight:bold">${resLvl.length}</td>
                  <td style="text-align:center">${lvlData.filter(r=>(r.status||'').toLowerCase()==='open').length}</td>
                  <td style="text-align:center">${lvlData.filter(r=>(r.status||'').toLowerCase()==='improving').length}</td>
                  <td style="text-align:center;color:#94a3b8">${lvlData.filter(r=>(r.status||'').toLowerCase()==='closed').length}</td>
                </tr>`;
              }).join('')}
            </tbody>
          </table>
        </div>`;

      const registerSummary = `
        <div class="page-break section">
          <div class="section-header dark">RISK REGISTER — SUMMARY <span style="float:right;font-size:13px;font-weight:400">Data as of ${monthYear}</span></div>
          <table>
            <thead><tr class="dark-header"><th>ID</th><th>RISK DESCRIPTION</th><th>INHERENT</th><th>RESIDUAL</th><th>CONTROLS</th><th>OWNER</th><th>DEADLINE</th></tr></thead>
            <tbody>
              ${reportData.map(r => `<tr>
                <td style="font-weight:bold;color:#1e3a5f">${r.sn || '—'}</td>
                <td style="font-weight:600">${r.risk_description || '—'}</td>
                <td>${riskBadge(r.inherent_risk_score)}</td>
                <td>${riskBadge(r.residual_risk_score)}</td>
                <td style="color:${(r.treatment_plan||'').toLowerCase().includes('strong')?'#27ae60':'#e67e22'};font-weight:bold">${r.treatment_plan || '—'}</td>
                <td style="font-size:11px">${r.owner || '—'}</td>
                <td style="font-size:11px">${r.review_date || 'Ongoing'}</td>
              </tr>`).join('')}
            </tbody>
          </table>
        </div>`;

      const priorityRisks = reportData.filter(r => ['extreme','high'].includes((r.inherent_risk_score||'').toLowerCase()));
      const priorityCards = priorityRisks.length ? `
        <div class="page-break section">
          <div class="section-header red">EXTREME &amp; HIGH PRIORITY RISKS</div>
          <div class="card-grid">
            ${priorityRisks.map(r => {
              const c = getRiskColor(r.inherent_risk_score);
              return `<div class="risk-card">
                <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px">
                  <span style="background:${c.bg};color:${c.text};padding:4px 12px;border-radius:4px;font-weight:bold;font-size:12px">${r.sn || '—'}</span>
                  <span style="background:${c.bg};color:${c.text};padding:4px 12px;border-radius:4px;font-weight:bold;font-size:11px">${r.inherent_risk_score || ''}</span>
                </div>
                <div style="font-size:14px;font-weight:bold;margin-bottom:10px">${r.risk_description || '—'}</div>
                ${r.causes ? `<div class="card-label">CAUSES</div><div class="card-text">${r.causes}</div>` : ''}
                ${r.consequences ? `<div class="card-label">CONSEQUENCES</div><div class="card-text">${r.consequences}</div>` : ''}
                ${r.existing_controls ? `<div class="card-label">EXISTING CONTROLS</div><div class="card-text">${r.existing_controls}</div>` : ''}
                ${r.action_plan ? `<div class="card-label">ACTIONS</div><div class="card-text">${r.action_plan}</div>` : ''}
                <div class="card-footer">Owner: ${r.owner || '—'} | Deadline: ${r.review_date || 'Ongoing'}</div>
              </div>`;
            }).join('')}
          </div>
        </div>` : '';

      const actionPlan = `
        <div class="page-break section">
          <div class="section-header dark">ACTION PLAN &amp; RISK TREATMENT</div>
          <table>
            <thead><tr class="dark-header"><th>ID</th><th>RISK</th><th>RATING</th><th>TREATMENT</th><th>ACTION SUMMARY</th><th>OWNER</th><th>DEADLINE</th><th>STATUS</th></tr></thead>
            <tbody>
              ${reportData.map(r => `<tr>
                <td style="font-weight:bold;color:#1e3a5f">${r.sn || '—'}</td>
                <td style="font-size:11px">${r.risk_description || '—'}</td>
                <td>${riskBadge(r.residual_risk_score)}</td>
                <td style="font-size:11px">${r.treatment || 'REDUCE'}</td>
                <td style="font-size:11px">${r.action_plan || '—'}</td>
                <td style="font-size:11px">${r.owner || '—'}</td>
                <td style="font-size:11px">${r.review_date || 'Ongoing'}</td>
                <td>${statusBadge(r.status)}</td>
              </tr>`).join('')}
            </tbody>
          </table>
        </div>`;

      return execSummary + registerSummary + priorityCards + actionPlan;
    };

    const buildSHESections = () => {
      const total = reportData.length;
      const open = reportData.filter(r => (r.status||'').toLowerCase() === 'open').length;
      const closed = reportData.filter(r => (r.status||'').toLowerCase() === 'closed').length;
      const high = reportData.filter(r => (r.priority||'').toLowerCase() === 'high').length;

      const cats = {};
      reportData.forEach(r => { const c = r.activity_category || 'Other'; cats[c] = (cats[c]||0)+1; });

      return `
        <div class="page-break section">
          <div class="section-header dark">SHE REGISTER — EXECUTIVE SUMMARY <span style="float:right;font-size:13px;font-weight:400">${monthYear}</span></div>
          <div style="display:flex;gap:16px;margin:20px 0">
            <div class="stat-card" style="background:#1e3a5f"><div class="stat-num">${total}</div><div class="stat-label">TOTAL RECORDS</div></div>
            <div class="stat-card" style="background:#e67e22"><div class="stat-num">${high}</div><div class="stat-label">HIGH PRIORITY</div></div>
            <div class="stat-card" style="background:#27ae60"><div class="stat-num">${open}</div><div class="stat-label">OPEN</div></div>
            <div class="stat-card" style="background:#3b82f6"><div class="stat-num">${closed}</div><div class="stat-label">CLOSED</div></div>
          </div>
          <div class="section-header dark" style="margin-top:20px">ACTIVITY BREAKDOWN BY CATEGORY</div>
          <table style="margin-top:10px">
            <thead><tr class="dark-header"><th>CATEGORY</th><th>COUNT</th><th>%</th></tr></thead>
            <tbody>${Object.entries(cats).map(([k,v])=>`<tr><td>${k}</td><td style="text-align:center;font-weight:bold">${v}</td><td style="text-align:center">${total?Math.round(v/total*100):0}%</td></tr>`).join('')}</tbody>
          </table>
        </div>
        <div class="page-break section">
          <div class="section-header dark">SHE ACTIVITY REGISTER</div>
          <table>
            <thead><tr class="dark-header"><th>ID</th><th>DATE</th><th>CATEGORY</th><th>LOCATION</th><th>DEPARTMENT</th><th>DESCRIPTION</th><th>OWNER</th><th>PRIORITY</th><th>STATUS</th></tr></thead>
            <tbody>${reportData.map(r=>`<tr>
              <td style="font-weight:bold">${r.action_id||'—'}</td>
              <td style="font-size:11px">${r.date||'—'}</td>
              <td style="font-size:11px">${r.activity_category||'—'}</td>
              <td style="font-size:11px">${r.location||'—'}</td>
              <td style="font-size:11px">${r.department||'—'}</td>
              <td style="font-size:11px">${r.description||'—'}</td>
              <td style="font-size:11px">${r.owner||'—'}</td>
              <td>${riskBadge(r.priority)}</td>
              <td>${statusBadge(r.status)}</td>
            </tr>`).join('')}</tbody>
          </table>
        </div>`;
    };

    const buildAccidentSections = () => {
      const total = reportData.length;
      const daysLost = reportData.reduce((s,r)=>s+Number(r.days_lost||0),0);
      const sources = {};
      reportData.forEach(r=>{const k=r.source_of_injury||'Other';sources[k]=(sources[k]||0)+1;});

      return `
        <div class="page-break section">
          <div class="section-header dark">ACCIDENT REGISTER — EXECUTIVE SUMMARY <span style="float:right;font-size:13px;font-weight:400">${monthYear}</span></div>
          <div style="display:flex;gap:16px;margin:20px 0">
            <div class="stat-card" style="background:#1e3a5f"><div class="stat-num">${total}</div><div class="stat-label">TOTAL INCIDENTS</div></div>
            <div class="stat-card" style="background:#c0392b"><div class="stat-num">${daysLost}</div><div class="stat-label">TOTAL DAYS LOST</div></div>
            <div class="stat-card" style="background:#e67e22"><div class="stat-num">${Object.keys(sources).length}</div><div class="stat-label">INJURY SOURCES</div></div>
          </div>
          <div class="section-header dark" style="margin-top:20px">INCIDENTS BY SOURCE OF INJURY</div>
          <table style="margin-top:10px">
            <thead><tr class="dark-header"><th>SOURCE OF INJURY</th><th>COUNT</th><th>%</th></tr></thead>
            <tbody>${Object.entries(sources).map(([k,v])=>`<tr><td>${k}</td><td style="text-align:center;font-weight:bold">${v}</td><td style="text-align:center">${total?Math.round(v/total*100):0}%</td></tr>`).join('')}</tbody>
          </table>
        </div>
        <div class="page-break section">
          <div class="section-header dark">ACCIDENT REGISTER — DETAIL</div>
          <table>
            <thead><tr class="dark-header"><th>IOD NO.</th><th>NAME</th><th>DATE</th><th>DEPARTMENT</th><th>DESIGNATION</th><th>SOURCE</th><th>NATURE</th><th>BODY PART</th><th>DAYS LOST</th><th>TREATMENT</th></tr></thead>
            <tbody>${reportData.map(r=>`<tr>
              <td style="font-weight:bold">${r.iod_number||'—'}</td>
              <td style="font-size:11px">${r.name_of_injured||'—'}</td>
              <td style="font-size:11px">${r.date_of_injury||'—'}</td>
              <td style="font-size:11px">${r.department||'—'}</td>
              <td style="font-size:11px">${r.designation||'—'}</td>
              <td style="font-size:11px">${r.source_of_injury||'—'}</td>
              <td style="font-size:11px">${r.nature_of_injury||'—'}</td>
              <td style="font-size:11px">${r.part_of_body_injured||'—'}</td>
              <td style="text-align:center;font-weight:bold">${r.days_lost||'0'}</td>
              <td style="font-size:11px">${r.medical_treatment||'—'}</td>
            </tr>`).join('')}</tbody>
          </table>
        </div>`;
    };

    const buildLossSections = () => {
      const total = reportData.length;
      const totalLoss = reportData.reduce((s,r)=>s+Number(r.estimated_loss_value||0),0);
      const totalRecovery = reportData.reduce((s,r)=>s+Number(r.estimated_recovery_value||0),0);
      const open = reportData.filter(r=>(r.status||'').toLowerCase()==='open').length;
      const fmt = v => new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format(v);
      const cats = {};
      reportData.forEach(r=>{const k=r.case_category||'Other';cats[k]=(cats[k]||0)+1;});

      return `
        <div class="page-break section">
          <div class="section-header dark">LOSS EVENTS — EXECUTIVE SUMMARY <span style="float:right;font-size:13px;font-weight:400">${monthYear}</span></div>
          <div style="display:flex;gap:16px;margin:20px 0;flex-wrap:wrap">
            <div class="stat-card" style="background:#1e3a5f"><div class="stat-num">${total}</div><div class="stat-label">TOTAL CASES</div></div>
            <div class="stat-card" style="background:#c0392b"><div class="stat-num" style="font-size:18px">${fmt(totalLoss)}</div><div class="stat-label">TOTAL LOSS VALUE</div></div>
            <div class="stat-card" style="background:#27ae60"><div class="stat-num" style="font-size:18px">${fmt(totalRecovery)}</div><div class="stat-label">EST. RECOVERY</div></div>
            <div class="stat-card" style="background:#e67e22"><div class="stat-num">${open}</div><div class="stat-label">OPEN CASES</div></div>
          </div>
          <div class="section-header dark" style="margin-top:20px">CASES BY CATEGORY</div>
          <table style="margin-top:10px">
            <thead><tr class="dark-header"><th>CASE CATEGORY</th><th>COUNT</th><th>%</th></tr></thead>
            <tbody>${Object.entries(cats).map(([k,v])=>`<tr><td>${k}</td><td style="text-align:center;font-weight:bold">${v}</td><td style="text-align:center">${total?Math.round(v/total*100):0}%</td></tr>`).join('')}</tbody>
          </table>
        </div>
        <div class="page-break section">
          <div class="section-header dark">LOSS EVENTS — CASE REGISTER</div>
          <table>
            <thead><tr class="dark-header"><th>CASE NO.</th><th>REFERENCE</th><th>EVENT</th><th>DEPT</th><th>DATE</th><th>CATEGORY</th><th>EST. LOSS</th><th>EST. RECOVERY</th><th>STATUS</th></tr></thead>
            <tbody>${reportData.map(r=>`<tr>
              <td style="font-weight:bold">${r.case_number||'—'}</td>
              <td style="font-size:11px">${r.loss_reference||'—'}</td>
              <td style="font-size:11px">${r.event_title||'—'}</td>
              <td style="font-size:11px">${r.department_id||'—'}</td>
              <td style="font-size:11px">${r.loss_date||'—'}</td>
              <td style="font-size:11px">${r.case_category||'—'}</td>
              <td style="font-size:11px;color:#c0392b;font-weight:bold">${r.estimated_loss_value?fmt(r.estimated_loss_value):'—'}</td>
              <td style="font-size:11px;color:#27ae60;font-weight:bold">${r.estimated_recovery_value?fmt(r.estimated_recovery_value):'—'}</td>
              <td>${statusBadge(r.status)}</td>
            </tr>`).join('')}</tbody>
          </table>
        </div>`;
    };

    const buildBCMSections = () => {
      const total = reportData.length;
      const avgReadiness = total ? Math.round(reportData.reduce((s,r)=>s+Number(r.readiness_score||0),0)/total) : 0;
      const statuses = {};
      reportData.forEach(r=>{const k=r.plan_status||'Unknown';statuses[k]=(statuses[k]||0)+1;});

      return `
        <div class="page-break section">
          <div class="section-header dark">BCM PLANS — EXECUTIVE SUMMARY <span style="float:right;font-size:13px;font-weight:400">${monthYear}</span></div>
          <div style="display:flex;gap:16px;margin:20px 0">
            <div class="stat-card" style="background:#1e3a5f"><div class="stat-num">${total}</div><div class="stat-label">TOTAL PLANS</div></div>
            <div class="stat-card" style="background:#3b82f6"><div class="stat-num">${avgReadiness}%</div><div class="stat-label">AVG READINESS</div></div>
            ${Object.entries(statuses).map(([k,v])=>`<div class="stat-card" style="background:#1e3a5f"><div class="stat-num">${v}</div><div class="stat-label">${k.toUpperCase()}</div></div>`).join('')}
          </div>
        </div>
        <div class="page-break section">
          <div class="section-header dark">BCM PLANS — REGISTER</div>
          <table>
            <thead><tr class="dark-header"><th>REFERENCE</th><th>PLAN NAME</th><th>DEPARTMENT</th><th>CRITICAL PROCESS</th><th>RTO (hrs)</th><th>RPO (hrs)</th><th>READINESS</th><th>STATUS</th></tr></thead>
            <tbody>${reportData.map(r=>`<tr>
              <td style="font-weight:bold">${r.plan_reference||'—'}</td>
              <td style="font-size:11px">${r.plan_name||'—'}</td>
              <td style="font-size:11px">${r.department_id||'—'}</td>
              <td style="font-size:11px">${r.critical_process||'—'}</td>
              <td style="text-align:center">${r.rto_hours||'—'}</td>
              <td style="text-align:center">${r.rpo_hours||'—'}</td>
              <td style="text-align:center;font-weight:bold;color:${Number(r.readiness_score||0)>=70?'#27ae60':'#e67e22'}">${r.readiness_score||'—'}%</td>
              <td>${statusBadge(r.plan_status)}</td>
            </tr>`).join('')}</tbody>
          </table>
        </div>`;
    };

    const buildGenericSection = () => `
      <div class="page-break section">
        <div class="section-header dark">${reportLabel.toUpperCase()} — REPORT</div>
        <table>
          <thead><tr class="dark-header">${columns.map(c=>`<th>${c.label}</th>`).join('')}</tr></thead>
          <tbody>${reportData.map(row=>`<tr>${columns.map(c=>`<td style="font-size:11px">${row[c.key]??'—'}</td>`).join('')}</tr>`).join('')}</tbody>
        </table>
      </div>`;

    const sectionContent =
      activeReport === 'risks' ? buildRiskSections() :
      activeReport === 'she' ? buildSHESections() :
      activeReport === 'accidents' ? buildAccidentSections() :
      activeReport === 'losses' ? buildLossSections() :
      activeReport === 'bcm' ? buildBCMSections() :
      buildGenericSection();

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${reportLabel} Dashboard Report</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; color: #1e293b; background: #fff; }

    /* ── Cover Page ── */
    .cover { background: #0f2044; color: white; min-height: 100vh; padding: 60px 70px; display: flex; flex-direction: column; justify-content: space-between; page-break-after: always; }
    .cover-top { font-size: 13px; letter-spacing: 3px; color: #c9a84c; font-weight: 600; margin-bottom: 40px; }
    .cover-body { flex: 1; display: flex; justify-content: space-between; align-items: center; }
    .cover-left h1 { font-size: 52px; font-weight: 900; color: white; line-height: 1.1; margin-bottom: 24px; }
    .cover-left h2 { font-size: 18px; letter-spacing: 3px; color: #94a3b8; font-weight: 400; margin-bottom: 30px; }
    .cover-divider { width: 80px; height: 3px; background: #c9a84c; margin-bottom: 30px; }
    .cover-meta { font-size: 13px; color: #94a3b8; line-height: 2; }
    .cover-meta strong { color: white; }
    .cover-logo { width: 180px; height: 180px; object-fit: contain; border: 3px solid rgba(255,255,255,0.15); border-radius: 12px; }
    .cover-footer { background: #1e3a5f; margin: 0 -70px -60px; padding: 16px 70px; font-size: 11px; letter-spacing: 4px; color: #94a3b8; text-align: center; }

    /* ── Filter Bar ── */
    .filter-bar { background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 16px; margin: 0 40px 24px; font-size: 11px; color: #475569; }
    .filter-bar strong { color: #1e293b; }

    /* ── Section ── */
    .section { padding: 30px 40px; }
    .page-break { page-break-before: always; }
    .section-header { color: white; padding: 14px 20px; font-size: 18px; font-weight: 800; letter-spacing: 1px; margin-bottom: 20px; border-radius: 4px; }
    .section-header.dark { background: #1e3a5f; }
    .section-header.red { background: #c0392b; }
    .section-header.orange { background: #e67e22; }

    /* ── Stat Cards ── */
    .stat-card { color: white; border-radius: 8px; padding: 20px; min-width: 140px; text-align: center; flex: 1; }
    .stat-num { font-size: 42px; font-weight: 900; line-height: 1; }
    .stat-label { font-size: 11px; font-weight: 700; letter-spacing: 1px; margin-top: 6px; }
    .stat-sub { font-size: 10px; margin-top: 4px; opacity: 0.8; }

    /* ── Tables ── */
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    th, td { padding: 9px 12px; border-bottom: 1px solid #e2e8f0; text-align: left; vertical-align: middle; }
    tr:nth-child(even) td { background: #f8fafc; }
    .dark-header th { background: #1e3a5f; color: white; font-size: 11px; letter-spacing: 0.5px; border: none; }

    /* ── Risk Cards ── */
    .card-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
    .risk-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; background: white; font-size: 12px; }
    .card-label { color: #c0392b; font-size: 10px; font-weight: 800; letter-spacing: 1px; margin: 10px 0 3px; }
    .card-text { font-size: 11px; color: #475569; margin-bottom: 4px; }
    .card-footer { margin-top: 12px; padding-top: 10px; border-top: 1px solid #fee2e2; font-size: 10px; color: #64748b; }

    @media print {
      .page-break { page-break-before: always; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>

  <!-- COVER PAGE -->
  <div class="cover">
    <div class="cover-top">MIDLANDS STATE UNIVERSITY</div>
    <div class="cover-body">
      <div class="cover-left">
        <h1>MSU Risk &amp; Loss Control</h1>
        <h2>${reportLabel.toUpperCase()} DASHBOARD</h2>
        <div class="cover-divider"></div>
        <div class="cover-meta">
          Generated: <strong>${generated}</strong><br>
          Report Period: <strong>${filterLine}</strong><br>
          Total Records: <strong>${reportData.length}</strong>
        </div>
      </div>
      <img class="cover-logo" src="${window.location.origin}/static/media/logo.jpg" onerror="this.style.display='none'" alt="MSU Logo" />
    </div>
    <div class="cover-footer">MIDLANDS STATE UNIVERSITY — RISK &amp; LOSS CONTROL</div>
  </div>

  <!-- FILTER SUMMARY -->
  <div class="filter-bar" style="margin-top:30px">
    <strong>Applied Filters:</strong> ${filterLine} &nbsp;|&nbsp; <strong>Records:</strong> ${reportData.length} &nbsp;|&nbsp; <strong>Generated:</strong> ${generated}
  </div>

  <!-- REPORT SECTIONS -->
  ${sectionContent}

</body>
</html>`;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 600);
  };

  const inputSx = {
    '& .MuiInputBase-root': { color: 'white', fontSize: '14px' },
    '& .MuiInputLabel-root': { color: '#94a3b8' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' },
    '& .MuiInputBase-input[type=date]::-webkit-calendar-picker-indicator': { filter: 'invert(1)' },
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', mb: 2 }}>Report Center</Typography>

      <Tabs
        value={activeReport}
        onChange={(_, v) => setActiveReport(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2,
          '& .MuiTab-root': { color: '#94a3b8', textTransform: 'none', fontWeight: 600, fontSize: '14px' },
          '& .Mui-selected': { color: '#10b981 !important' },
          '& .MuiTabs-indicator': { backgroundColor: '#10b981' },
        }}
      >
        {availableTypes.map(t => <Tab key={t.key} value={t.key} label={t.label} />)}
      </Tabs>

      {/* Filters & actions */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
        <TextField
          type="date"
          label="From"
          size="small"
          value={fromDate}
          onChange={e => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ ...inputSx, width: 150 }}
        />
        <TextField
          type="date"
          label="To"
          size="small"
          value={toDate}
          onChange={e => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ ...inputSx, width: 150 }}
        />
        <TextField
          select
          label="Department"
          size="small"
          value={departmentFilter}
          onChange={e => setDepartmentFilter(e.target.value)}
          sx={{ ...inputSx, width: 180 }}
          SelectProps={{ sx: { fontSize: '13px' } }}
        >
          <MenuItem value=""><em>All Departments</em></MenuItem>
          {departments.map(dept => (
            <MenuItem key={dept.department_id || dept.id} value={dept.department_id || dept.id}>
              {dept.department_name || dept.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Status"
          size="small"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          sx={{ ...inputSx, width: 140 }}
          SelectProps={{ sx: { fontSize: '13px' } }}
        >
          <MenuItem value=""><em>All Statuses</em></MenuItem>
          {(filterOptions.status || []).map(status => (
            <MenuItem key={status} value={status}>{status}</MenuItem>
          ))}
        </TextField>
        
        {/* SHE Register Additional Filters */}
        {activeReport === 'she' && (
          <>
            <TextField
              select
              label="Activity Category"
              size="small"
              value={sheCategoryFilter}
              onChange={e => setSheCategoryFilter(e.target.value)}
              sx={{ ...inputSx, width: 160 }}
              SelectProps={{ sx: { fontSize: '13px' } }}
            >
              <MenuItem value=""><em>All Categories</em></MenuItem>
              {(filterOptions.activity_category || []).map(cat => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Priority"
              size="small"
              value={shePriorityFilter}
              onChange={e => setShePriorityFilter(e.target.value)}
              sx={{ ...inputSx, width: 130 }}
              SelectProps={{ sx: { fontSize: '13px' } }}
            >
              <MenuItem value=""><em>All Priorities</em></MenuItem>
              {(filterOptions.priority || []).map(pri => (
                <MenuItem key={pri} value={pri}>{pri}</MenuItem>
              ))}
            </TextField>
          </>
        )}
        
        {/* Accident Register Additional Filters */}
        {activeReport === 'accidents' && (
          <>
            <TextField
              select
              label="Source of Injury"
              size="small"
              value={accidentSourceFilter}
              onChange={e => setAccidentSourceFilter(e.target.value)}
              sx={{ ...inputSx, width: 170 }}
              SelectProps={{ sx: { fontSize: '13px' } }}
            >
              <MenuItem value=""><em>All Sources</em></MenuItem>
              {(filterOptions.source_of_injury || []).map(source => (
                <MenuItem key={source} value={source}>{source}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Nature of Injury"
              size="small"
              value={accidentNatureFilter}
              onChange={e => setAccidentNatureFilter(e.target.value)}
              sx={{ ...inputSx, width: 170 }}
              SelectProps={{ sx: { fontSize: '13px' } }}
            >
              <MenuItem value=""><em>All Nature of Injuries</em></MenuItem>
              {(filterOptions.nature_of_injury || []).map(nature => (
                <MenuItem key={nature} value={nature}>{nature}</MenuItem>
              ))}
            </TextField>
          </>
        )}
        
        {/* Risk Register Additional Filters */}
        {activeReport === 'risks' && (
          <>
            <TextField
              select
              label="Risk Category"
              size="small"
              value={riskCategoryFilter}
              onChange={e => setRiskCategoryFilter(e.target.value)}
              sx={{ ...inputSx, width: 150 }}
              SelectProps={{ sx: { fontSize: '13px' } }}
            >
              <MenuItem value=""><em>All Categories</em></MenuItem>
              {(filterOptions.category || []).map(cat => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Likelihood"
              size="small"
              value={riskLikelihoodFilter}
              onChange={e => setRiskLikelihoodFilter(e.target.value)}
              sx={{ ...inputSx, width: 140 }}
              SelectProps={{ sx: { fontSize: '13px' } }}
            >
              <MenuItem value=""><em>All</em></MenuItem>
              {(filterOptions.inherent_likelihood || []).map(lik => (
                <MenuItem key={lik} value={lik}>{lik}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Consequence"
              size="small"
              value={riskConsequenceFilter}
              onChange={e => setRiskConsequenceFilter(e.target.value)}
              sx={{ ...inputSx, width: 140 }}
              SelectProps={{ sx: { fontSize: '13px' } }}
            >
              <MenuItem value=""><em>All</em></MenuItem>
              {(filterOptions.inherent_consequence || []).map(cons => (
                <MenuItem key={cons} value={cons}>{cons}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Min Score"
              size="small"
              value={riskScoreMin}
              onChange={e => { setRiskScoreMin(e.target.value); if (riskScoreMax && Number(e.target.value) > Number(riskScoreMax)) setRiskScoreMax(''); }}
              sx={{ ...inputSx, width: 110 }}
              SelectProps={{ sx: { fontSize: '13px' } }}
            >
              <MenuItem value=""><em>Any</em></MenuItem>
              {(filterOptions.inherent_risk_scores || [])
                .filter(s => !riskScoreMax || Number(s) <= Number(riskScoreMax))
                .map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField
              select
              label="Max Score"
              size="small"
              value={riskScoreMax}
              onChange={e => { setRiskScoreMax(e.target.value); if (riskScoreMin && Number(e.target.value) < Number(riskScoreMin)) setRiskScoreMin(''); }}
              sx={{ ...inputSx, width: 110 }}
              SelectProps={{ sx: { fontSize: '13px' } }}
            >
              <MenuItem value=""><em>Any</em></MenuItem>
              {(filterOptions.inherent_risk_scores || [])
                .filter(s => !riskScoreMin || Number(s) >= Number(riskScoreMin))
                .map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </>
        )}
        
        <Button variant="contained" size="small" onClick={handleGenerate}
          sx={{ background: '#10b981', '&:hover': { background: '#059669' }, textTransform: 'none', fontWeight: 600 }}>Generate</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleCsvExport}
          disabled={!reportData.length}
          sx={{ borderColor: '#3b82f6', color: '#3b82f6', textTransform: 'none', '&:hover': { borderColor: '#2563eb', background: 'rgba(59,130,246,0.08)' } }}>CSV</Button>
        <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={handlePrint}
          disabled={!reportData.length}
          sx={{ borderColor: '#8b5cf6', color: '#8b5cf6', textTransform: 'none', '&:hover': { borderColor: '#7c3aed', background: 'rgba(139,92,246,0.08)' } }}>Print / PDF</Button>
      </Box>

      {/* Report table */}
      {reportLoading ? (
        <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress sx={{ color: '#10b981' }} /></Box>
      ) : reportError ? (
        <Paper sx={{ p: 4, textAlign: 'center', background: 'rgba(239,68,68,0.06)', borderRadius: '16px', border: '1px solid rgba(239,68,68,0.15)' }}>
          <Typography sx={{ color: '#ef4444' }}>{reportError}</Typography>
        </Paper>
      ) : (
        <TableContainer ref={printRef} component={Paper} sx={{ background: 'rgba(15,23,42,0.75)', borderRadius: '18px', border: '1px solid rgba(255,255,255,0.08)' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { color: '#94a3b8', fontWeight: 'bold', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' } }}>
                {columns.map(c => <TableCell key={c.key}>{c.label}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.map((row, idx) => (
                <TableRow key={idx} sx={{ '& td': { color: '#f1f5f9', borderColor: 'rgba(255,255,255,0.05)', fontSize: '13px' } }}>
                  {columns.map(c => <TableCell key={c.key}>{row[c.key] ?? '—'}</TableCell>)}
                </TableRow>
              ))}
              {reportData.length === 0 && (
                <TableRow><TableCell colSpan={columns.length} align="center" sx={{ color: '#64748b', py: 6 }}>No records found for the selected criteria.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          {reportData.length > 0 && (
            <Box sx={{ p: 1.5, textAlign: 'right', color: '#64748b', fontSize: '12px' }}>
              {reportData.length} record{reportData.length !== 1 ? 's' : ''}
            </Box>
          )}
        </TableContainer>
      )}
    </Box>
  );
};

export default ReportsAnalytics;
